import { Box, Grid, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import { CircularProgressWithLabel } from 'components/shared/CircularProgressWithLabel';
import { observer } from 'mobx-react-lite';
import DriveFolderUpload from '@mui/icons-material/DriveFolderUpload';
import { CategoryStatus } from 'dtos/diligence/common';

interface BadgeProps {
  status: CategoryStatus;
}

const Badge = styled('div', {
  shouldForwardProp: (prop) => prop !== 'status',
})<BadgeProps>(({ theme, status }) => ({
  display: 'inline-block',
  width: '16px',
  height: '16px',
  backgroundColor: theme.palette[status].main,
  border: `1px solid ${theme.palette[status].dark}`,
  borderRadius: '4px',
  marginRight: '8px',
}));

type DiligenceProgressProps = {
  sx?: any;
  percentComplete: number;
  incomplete: number;
  submitted: number;
};

const DiligenceProgress = observer(({ sx, percentComplete, incomplete, submitted }: DiligenceProgressProps) => {
  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        padding: {
          xs: '8px',
          sm: '24px',
        },
        alignItems: 'flex-start',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        ...sx,
      }}
    >
      <CircularProgressWithLabel
        value={percentComplete}
        showCheck={percentComplete === 100}
        label={
          <>
            {Math.round(percentComplete) + '%'}
            <br />
            Uploaded
          </>
        }
        size={104}
        color={percentComplete === 100 ? 'success' : 'primary'}
        data-testid="progress-meter"
      />
      <div style={{ marginLeft: 42, whiteSpace: 'nowrap' }} data-testid="progress-stats">
        <Typography
          component="div"
          variant="overline"
          sx={{ display: 'flex', alignItems: 'center', lineHeight: 1, marginBottom: '8px' }}
        >
          <DriveFolderUpload sx={{ marginRight: '4px' }} />
          {xsScreen ? 'Progress' : 'Due Diligence Progress'}
        </Typography>
        <Grid container columnSpacing={3} rowSpacing={0}>
          <Grid item xs={12} lg="auto">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Badge status="incomplete" />
              <Typography variant="subtitle2">
                <strong style={{ marginRight: 4 }}>{incomplete}</strong>
              </Typography>
              <Typography variant="caption">{xsScreen ? 'Waiting' : 'Waiting for Submission'}</Typography>
            </div>
          </Grid>
          <Grid item xs={12} lg="auto">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Badge status="submitted" />
              <Typography variant="subtitle2">
                <strong style={{ marginRight: 4 }}>{submitted}</strong>
              </Typography>
              <Typography variant="caption">Submitted</Typography>
            </div>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
});

export { DiligenceProgress };
